import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Text,
  Button,
  Input,
  Textarea,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarButton,
  Tooltip,
  Spinner,
  Link,
} from "@fluentui/react-components";
import {
  Building24Regular,
  ArrowLeft20Regular,
} from "@fluentui/react-icons";
import { useDevice } from "../../../model";
import { useAppStyles } from "../../styles/appStyles";
import { config } from "../../../config/environment";

interface AddCompanyPageProps {
  onBack: () => void;
  onSuccess?: () => void;
}

const AddCompanyPage: React.FC<AddCompanyPageProps> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const styles = useAppStyles();
  const { fingerprint, isTrial, addCompanyToTrialSubscription, refreshSubscription } = useDevice();

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [clientIdError, setClientIdError] = useState<string | null>(null);
  const [clientSecretError, setClientSecretError] = useState<string | null>(null);

  const validateClientId = (value: string): boolean => {
    // UUID format: 8-4-4-4-12 hexadecimal characters
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidPattern.test(value);
  };

  const validateClientSecret = (value: string): boolean => {
    // 64 hexadecimal characters (no dashes)
    const hexPattern = /^[0-9a-f]{64}$/i;
    return hexPattern.test(value);
  };

  const handleClientIdChange = (value: string) => {
    const trimmedValue = value.trim().toLowerCase();
    setClientId(trimmedValue);
    
    if (trimmedValue && !validateClientId(trimmedValue)) {
      setClientIdError(t("add_company.error.invalid_client_id", "Client ID musi mieć format UUID (np. 0f5ce8d2-1d97-43cd-a0f4-4dc7f82ef115)"));
    } else {
      setClientIdError(null);
    }
  };

  const handleClientSecretChange = (value: string) => {
    const trimmedValue = value.trim().toLowerCase();
    setClientSecret(trimmedValue);
    
    if (trimmedValue && !validateClientSecret(trimmedValue)) {
      setClientSecretError(t("add_company.error.invalid_client_secret", "Client Secret musi mieć 64 znaki."));
    } else {
      setClientSecretError(null);
    }
  };

  const handleAddCompany = async () => {
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();

    if (!trimmedClientId || !trimmedClientSecret) {
      setError(t("add_company.error.fields_required", "Wszystkie pola są wymagane"));
      return;
    }

    if (!validateClientId(trimmedClientId)) {
      setError(t("add_company.error.invalid_client_id", "Client ID musi mieć format UUID (np. 0f5ce8d2-1d97-43cd-a0f4-4dc7f82ef115)"));
      return;
    }

    if (!validateClientSecret(trimmedClientSecret)) {
      setError(t("add_company.error.invalid_client_secret", "Client Secret musi mieć 64 znaki."));
      return;
    }

    setIsAdding(true);
    setError(null);

    try {
      console.log("🏢 Starting add company process...");

      const apiUrl = config.api.baseUrl;
      
      // For Trial mode, use FindCompany endpoint (local only, no backend persist)
      // For registered subscription, use AddCompany endpoint (persisted to backend)
      const endpoint = isTrial ? "/api/v1/Subscription/findcompany" : "/api/v1/Subscription/addcompany";
      const url = `${apiUrl}${endpoint}`;

      console.log(`📡 Calling API (${isTrial ? "Trial - FindCompany" : "Registered - AddCompany"}):`, url);

      const payload = isTrial
        ? { clientId: trimmedClientId, clientSecret: trimmedClientSecret } // FindCompany payload
        : { deviceFingerprint: fingerprint, clientId: trimmedClientId, clientSecret: trimmedClientSecret }; // AddCompany payload

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = t("add_company.error.unknown", "Nieznany błąd dodawania firmy");

        try {
          const contentType = response.headers.get("content-type");
          if (contentType && contentType.includes("application/json")) {
            const errorData = await response.json();
            console.error("❌ API Error response:", errorData);

            // Priority: details > error > message > default
            if (errorData.details) {
              errorMessage = errorData.details;
            } else if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          } else {
            const textError = await response.text();
            errorMessage = textError || `${t("add_company.error.http", "Błąd HTTP")} ${response.status}`;
          }
        } catch (parseError) {
          console.error("❌ Error parsing error response:", parseError);
          errorMessage = `${t("add_company.error.http", "Błąd HTTP")} ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log("✅ Company data received:", data);

      // For Trial mode, add company locally using DeviceContext
      if (isTrial) {
        addCompanyToTrialSubscription(data);
      } else {
        // For registered subscription, refresh to get updated data from backend
        await refreshSubscription();
      }

      setSuccess(true);
      setClientId("");
      setClientSecret("");

      // Po krótkiej chwili wróć lub wywołaj callback sukcesu
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        } else {
          onBack();
        }
      }, 2000);
    } catch (err) {
      console.error("❌ Add company error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsAdding(false);
    }
  };

  const handleInstructionsClick = () => {
    const url = "https://bdo.mos.gov.pl/wp-content/uploads/2023/04/BDO_IS_Instrukcja-generowania-kluczy-API.pdf";
    window.open(url, "_blank");
  };

  return (
    <div className={styles.appPageContainer}>
      <div className={styles.appPageHeader}>
        <Building24Regular className={styles.appPageHeaderIcon} />
        <Text className={styles.appPageTitle}>
          {t("add_company.title", "Dodaj firmę z BDO")}
        </Text>
      </div>

      <Toolbar className={styles.toolbar}>
        <Tooltip
          content={t("add_company.toolbar.back_tooltip", "Powrót do poprzedniej strony")}
          relationship="description"
        >
          <ToolbarButton
            className={styles.toolbarButton}
            aria-label={t("add_company.toolbar.back", "Powrót")}
            icon={<ArrowLeft20Regular className={styles.backIcon} />}
            onClick={onBack}
            disabled={isAdding}
          >
            {t("add_company.toolbar.back", "Powrót")}
          </ToolbarButton>
        </Tooltip>
      </Toolbar>

      <div className={styles.appPageContent}>
        {/* Success Message */}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>
                {t("add_company.success.title", "Firma dodana pomyślnie!")}
              </MessageBarTitle>
              {t("add_company.success.message", "Firma została pomyślnie dodana. Przekierowywanie...")}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Error Message */}
        {error && !success && (
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {t("add_company.error.title", "Błąd dodawania firmy")}
              </MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        )}

        {/* Instructions */}
        {!success && (
          <>
            {/* Info Section */}
            <MessageBar intent="info" style={{ marginBottom: "16px" }}>
              <MessageBarBody>
                <MessageBarTitle>
                  {t("add_company.info.title", "Informacja")}
                </MessageBarTitle>
                <Text size={200}>
                  {t("add_company.info.description", "Aby dodać firmę do subskrypcji, potrzebujesz kluczy API z systemu BDO z ")}{" "}
                  <Link onClick={handleInstructionsClick} style={{ textDecoration: "underline", fontWeight: 600 }}>
                    {t("add_company.info.instructions_link", "oficjalnej instrukcji BDO")}
                  </Link>
                  .
                </Text>
              </MessageBarBody>
            </MessageBar>

            {/* Client ID Input */}
            <div style={{ marginBottom: "16px" }}>
              <Text weight="semibold" style={{ display: "block", marginBottom: "8px" }}>
                {t("add_company.client_id_label", "Client ID")}
                <span style={{ color: "#a80000" }}> *</span>
              </Text>
              <Input
                type="text"
                value={clientId}
                onChange={(e, data) => handleClientIdChange(data.value)}
                placeholder={t("add_company.client_id_placeholder", "Wprowadź Client ID z BDO")}
                pattern="[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
                maxLength={36}
                disabled={isAdding}
                style={{ width: "100%" }}
              />
              {clientIdError && (
                <Text size={200} style={{ color: "#d32f2f", marginTop: "4px", display: "block" }}>
                  {clientIdError}
                </Text>
              )}
              <Text size={200} style={{ color: "#666", marginTop: "4px", display: "block" }}>
                {t("add_company.client_id_hint", "Format: 8-4-4-4-12 znaków")}
              </Text>
            </div>

            {/* Client Secret Input */}
            <div style={{ marginBottom: "16px" }}>
              <Text weight="semibold" style={{ display: "block", marginBottom: "8px" }}>
                {t("add_company.client_secret_label", "Client Secret")}
                <span style={{ color: "#a80000" }}> *</span>
              </Text>
              <Textarea
                value={clientSecret}
                onChange={(e, data) => handleClientSecretChange(data.value)}
                placeholder={t("add_company.client_secret_placeholder", "Wprowadź Client Secret z BDO")}
                resize="none"
                rows={3}
                maxLength={64}
                disabled={isAdding}
                style={{ width: "100%" }}
              />
              {clientSecretError && (
                <Text size={200} style={{ color: "#d32f2f", marginTop: "4px", display: "block" }}>
                  {clientSecretError}
                </Text>
              )}
              <Text size={200} style={{ color: "#666", marginTop: "4px", display: "block" }}>
                {t("add_company.client_secret_hint", "Format: 64 znaki hex (np. 48115dc6...e39066)")}
              </Text>
            </div>

            {/* Add Button */}
            <div style={{ marginTop: "24px" }}>
              <Button
                appearance="primary"
                onClick={handleAddCompany}
                disabled={isAdding || !clientId.trim() || !clientSecret.trim() || !!clientIdError || !!clientSecretError}
                icon={isAdding ? <Spinner size="tiny" /> : undefined}
                style={{ width: "100%" }}
              >
                {isAdding
                  ? t("add_company.button.adding", "Dodawanie...")
                  : t("add_company.button.add", "Dodaj firmę")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddCompanyPage;
